import styles from './productForm.module.css'
import { Data, DataTypeForm, DefaultUnitTypeId, isSupportedVatTaxCode, Labels, QuickFormModal } from "../../../../../features/data-types"
import { PriceField, FieldType, FieldValue, FormField, ImageField, ModalId, NumberField, O, Option, option, RadioField, SelectField, StaticValue, TextArea, useUIActions, Validatable, PercentField, useEffectAfterMount, FormSeparator, UIPermission } from "../../../../../features/ui"
import { MeasureLabel, Product, ProductPrices, ProductType } from '../../../state/types'
import { useNavigate } from "react-router-dom"
import { TableName } from "../../../../../tables"
import { useEffect, useMemo, useState } from "react"
import { Supplier, SupplierQuickForm, SupplierField, usePurchasesHooks } from "../../../../purchases"
import { UnitTypeCategory, UnitTypeName, useUnitTypeLoaders } from "../../../../stock"
import { useGenerateNextValue2 } from '../../../../../tables/hooks'
import { useFileActions } from '../../../../../features/files'
import { toList } from '../../../../../utils/listUtils'
import { useActions } from './actions'
import { TaxType, useAdministrationHooks, useTaxLoaders } from '../../../../administration'
import { PriceChoice } from './types'
import { usePricesHooks } from '../../../../operations'
import { getMeasureLabel } from '../../../utils/measuresUtils'
import { MeasureType, MeasuresTable, MeasureFormModal, Discount, DiscountTable, DiscountFormModal } from '../../../../common'
import { useSystemStateHooks } from '../../../../system'

// The purchase price and its bonuses
const COST_FIELDS = ['basePurchasePrice', 'hasBonuses', 'bonuses', 'purchasePrice']
// The list price is the purchase price plus the yield: without purchase price there is no price to define
const LIST_PRICE_FIELDS = ['priceChoice', 'saleYield', 'salePrice', 'salePriceWithVat']

type Props = {
    product?: Product
    productPrices?: ProductPrices
}

export const ProductForm = (props: Props) => {
    const { product, productPrices } = props
    const createMode = !product

    const [code, setCode] = useState(product?.code || 1)
    const [supplierId, setSupplierId] = useState<O<string>>(product?.supplierId)
    const [categoryId, setCategoryId] = useState<O<string>>(product?.categoryId)
    const [imageFile, setImageFile] = useState<File>()
    const [description, setDescription] = useState<O<string>>(product?.description)
    const [type, setType] = useState<O<ProductType>>(product?.type || ProductType.UNITARY)
    const [units, setUnits] = useState<O<number>>(product?.units || 1)
    const [unitTypeId, setUnitTypeId] = useState<O<string>>(product?.unitTypeId || DefaultUnitTypeId)
    const [secondaryUnits, setSecondaryUnits] = useState<O<number>>(product?.secondaryUnits)
    const [secondaryUnitTypeId, setSecondaryUnitTypeId] = useState<O<string>>(product?.secondaryUnitTypeId)
    const [measuresData, setMeasuresData] = useState<Data[]>(product?.measures || [])
    const [measuresValidation, setMeasuresValidation] = useState<string>()
    const [unitMeasuresData, setUnitMeasuresData] = useState<Data[]>(product?.unitMeasures || [])
    const [currentMeasure, setCurrentMeasure] = useState<Data>()
    const [isCurrentMainMeasure, setIsCurrentMainMeasure] = useState<boolean>(true)
    const [measureLabel, setMeasureLabel] = useState<O<MeasureLabel>>(product ? product.measureLabel : MeasureLabel.UNITS)
    const [basePurchasePrice, setBasePurchasePrice] = useState<O<number>>(productPrices?.purchasePrice)
    const [hasBonuses, setHasBonuses] = useState<boolean>(!!productPrices && productPrices.bonuses.length > 0)
    const [bonusesData, setBonusesData] = useState<Partial<Discount>[]>(productPrices?.bonuses || [])
    const [currentBonus, setCurrentBonus] = useState<Partial<Discount>>()
    const [vatTaxId, setVatTaxId] = useState<O<string>>(productPrices?.vatTaxId)
    const [priceChoice, setPriceChoice] = useState<O<PriceChoice>>(productPrices?.saleYield ? PriceChoice.PRODUCT_YIELD : PriceChoice.GLOBAL_YIELD)
    const [saleYield, setSaleYield] = useState<O<number>>(productPrices?.saleYield)
    const [salePrice, setSalePrice] = useState<O<number>>(0)
    const [salePriceWithVat, setSalePriceWithVat] = useState<O<number>>(0)
    const [loaded, setLoaded] = useState<boolean>(false)
    const [loted, setLoted] = useState<boolean>(!!product?.loted)
    const [allowNegativeStock, setAllowNegativeStock] = useState<boolean>(!!product?.allowNegativeStock)
    const { getNegativeStockCategoryIds } = usePurchasesHooks()
    const isNegativeStockCategory = !!categoryId && getNegativeStockCategoryIds().includes(categoryId)

    const { userHasPermission } = useSystemStateHooks()
    // Who can't see the cost (e.g. sales) defines the list price or the yield over the purchase price already loaded,
    // without seeing it: a new product is saved without purchase price for purchases to complete it
    const canSeeCost = userHasPermission(UIPermission.READ_PRODUCT_PURCHASE_PRICE)
    const { getParentSaleYield } = useAdministrationHooks()
    const pricesHooks = usePricesHooks()
    const generateNextValue = useGenerateNextValue2()
    const navigate = useNavigate()

    const unitTypeLoaders = useUnitTypeLoaders()
    const taxLoaders = useTaxLoaders()

    const { submitProduct } = useActions()
    const { openModal } = useUIActions()
    const { fetchFile } = useFileActions()

    const parentSaleYield = useMemo(() => getParentSaleYield(categoryId), [categoryId])
    const purchasePrice = useMemo(() => pricesHooks.purchasePrice(basePurchasePrice || 0, bonusesData as Discount[]), [bonusesData, basePurchasePrice])

    const load = async () => {
        if (createMode) {
            const newCode = await generateNextValue(TableName.PRODUCTS, 'code')
            setCode(newCode)
            setSaleYield(parentSaleYield)
        } else if (product.imageUrl) {
            const file = await fetchFile(product.imageUrl, true)
            setImageFile(file)
        }
    }

    useEffect(() => {
        load().then(() => setLoaded(true))
    }, [])

    useEffectAfterMount(() => {
        const isBulk = type === ProductType.BULK
        const isPackage = type === ProductType.PACKAGE
        setUnits(isBulk ? undefined : 1)
        if (isBulk) {
            setUnitMeasuresData([])
            setMeasureLabel(undefined)
        } else if (isPackage) {
            setUnitTypeId(DefaultUnitTypeId)
            const squareMeter = unitTypeLoaders().findUnitTypeByName(UnitTypeName.SQUARE_METER)
            setSecondaryUnitTypeId(squareMeter!.id)
            setMeasureLabel(MeasureLabel.BOX)
        }
        if (!isPackage) {
            setMeasuresData(prevMeasuresData => prevMeasuresData.filter(measureData => measureData.type !== MeasureType.PACKAGE_UNITS))
            setSecondaryUnits(undefined)
            setSecondaryUnitTypeId(undefined)
        }
    }, [type])

    useEffect(() => {
        if (priceChoice && [PriceChoice.GLOBAL_YIELD, PriceChoice.PRODUCT_YIELD].includes(priceChoice)) {
            const isGlobalYield = priceChoice === PriceChoice.GLOBAL_YIELD
            const newSalePrice = pricesHooks.basePrice(purchasePrice || 0, isGlobalYield ? parentSaleYield : saleYield)
            setSalePrice(newSalePrice)
            setSalePriceWithVat(vatTaxId ? pricesHooks.product.applyVat(newSalePrice, vatTaxId) : newSalePrice)
            isGlobalYield && setSaleYield(parentSaleYield)
        }
    }, [priceChoice, purchasePrice, saleYield, parentSaleYield, vatTaxId])

    useEffect(() => {
        if (priceChoice === PriceChoice.PRODUCT_PRICE) {
            setSaleYield(pricesHooks.saleYield(purchasePrice || 0, salePrice || 0))
            setSalePriceWithVat(vatTaxId ? pricesHooks.product.applyVat(salePrice || 0, vatTaxId) : salePrice || 0)
        } else if (priceChoice === PriceChoice.PRODUCT_PRICE_WITH_VAT) {
            const newSalePrice = vatTaxId ? pricesHooks.product.removeVat(salePriceWithVat || 0, vatTaxId) : salePriceWithVat || 0
            setSaleYield(pricesHooks.saleYield(purchasePrice || 0, newSalePrice))
            setSalePrice(newSalePrice)
        }
    }, [priceChoice, purchasePrice, salePrice, salePriceWithVat, vatTaxId])

    const unitTypes = useMemo(() => unitTypeLoaders().findAllUnitTypes(), [])
    const unitTypeOptions = useMemo(() => unitTypes.map(unitType => option(unitType.id, unitType.name)), [unitTypes])

    const vatTaxOptions = useMemo(() => {
        const { findTaxByCode, findAllTaxes } = taxLoaders()
        return findAllTaxes()
            .filter(tax =>
                tax.type === TaxType.VAT &&
                isSupportedVatTaxCode(tax.code) &&
                (!tax.archived || findTaxByCode(tax.code)?.id === vatTaxId)
            )
            .map(tax => option(tax.id, tax.name))
    }, [vatTaxId])

    const purchasePriceHint = useMemo(() => {
        const unitType = unitTypeLoaders().findUnitType(unitTypeId)
        const bulkHint = type === ProductType.BULK && unitType ? `Precio por ${unitType.name.toLowerCase()}. ` : undefined
        return `${bulkHint || ''}Precio de compra sin impuestos incluidos.`
    }, [unitTypeId, type])

    const priceChoiceOptions: Option[] = useMemo(() => [
        option(PriceChoice.GLOBAL_YIELD, `Usar rentabilidad general (${parentSaleYield}%)`),
        option(PriceChoice.PRODUCT_YIELD, 'Definir rentabilidad del producto'),
        option(PriceChoice.PRODUCT_PRICE, 'Definir precio de lista s/IVA'),
        option(PriceChoice.PRODUCT_PRICE_WITH_VAT, 'Definir precio de lista c/IVA'),
    ], [parentSaleYield])

    const measureTypes = useMemo(() => {
        const data = isCurrentMainMeasure ? measuresData : unitMeasuresData
        const baseMeasureTypes = Object.values(MeasureType).filter(measureType => type === ProductType.PACKAGE && isCurrentMainMeasure ? true : measureType !== MeasureType.PACKAGE_UNITS)
        return baseMeasureTypes.filter(measureType => currentMeasure?.type === measureType || !data.map(measureData => measureData.type).includes(measureType))
    }, [measuresData, currentMeasure, isCurrentMainMeasure, type])

    const measuresTitle = useMemo(() => {
        const unitType = unitTypeLoaders().findUnitType(unitTypeId)
        return `Medidas ${type === ProductType.BULK && unitType ? 'de 1 ' + unitType.name.toLowerCase() :
            (type === ProductType.PACKAGE ? 'de la caja' : 'del producto')}`
    }, [type, unitTypeId])

    const onCreateSupplier = () => openModal(ModalId.QUICK_FORM)

    const onChangeCategory = (value?: FieldValue) => setCategoryId(value as O<string>)

    const onChangeLoted = (value?: FieldValue) => setLoted(!!value)

    const onChangeAllowNegativeStock = (value?: FieldValue) => setAllowNegativeStock(!!value)

    const onChangeType = (value?: FieldValue) => {
        const newType = value as O<ProductType>
        setType(newType)
        if (newType === ProductType.BULK) {
            setUnitTypeId(unitTypes.find(unitType => unitType.name === UnitTypeName.TON)?.id)
        }
    }

    const onChangeUnits = (value?: FieldValue) => setUnits(value as O<number>)

    const onChangeUnitType = (value?: FieldValue) => setUnitTypeId(value as O<string>)

    const onChangeSecondaryUnits = (value?: FieldValue) => setSecondaryUnits(value as O<number>)

    const openMeasureModal = () => openModal(ModalId.MEASURE_FORM)

    const onAddMeasure = (mainMeasures = true) => () => {
        setIsCurrentMainMeasure(mainMeasures)
        setCurrentMeasure(undefined)
        mainMeasures && setMeasuresValidation(undefined)
        openMeasureModal()
    }
    
    const onEditMeasure = (mainMeasures = true) => (measureType: string) => {
        setIsCurrentMainMeasure(mainMeasures)
        const data = mainMeasures ? measuresData : unitMeasuresData
        setCurrentMeasure(data.find(measureData => measureData.type === measureType))
        mainMeasures && setMeasuresValidation(undefined)
        openMeasureModal()
    }
    
    const onRemoveMeasure = (mainMeasures = true) => (measureType: string) => {
        const data = mainMeasures ? measuresData : unitMeasuresData
        const newMeasuresData = data.filter(measureData => measureData.type !== measureType)
        const setData = mainMeasures ? setMeasuresData : setUnitMeasuresData
        setData(newMeasuresData)
    }

    const onChangeMeasureLabel = (value?: FieldValue) => setMeasureLabel(value as O<MeasureLabel>)

    const onChangeBasePurchasePrice = (value?: FieldValue) => setBasePurchasePrice(value as O<number>)

    const onChangeHasBonuses = (value?: FieldValue) => {
        const newHasBonuses = !!value as boolean
        setHasBonuses(newHasBonuses)
        !newHasBonuses && setBonusesData([])
    }

    const onAddBonus = () => {
        setCurrentBonus(undefined)
        openModal(ModalId.DISCOUNT_FORM)
    }
    
    const onEditBonus = (bonusName: string) => {
        setCurrentBonus(bonusesData.find(bonusData => bonusData.name === bonusName))
        openModal(ModalId.DISCOUNT_FORM)
    }
    
    const onRemoveBonus = (bonusName: string) => {
        const newBonusesData = bonusesData.filter(bonusData => bonusData.name !== bonusName)
        setBonusesData(newBonusesData)
    }

    const onChangeVatTax = (value?: FieldValue) => setVatTaxId(value as O<string>)
    
    const onChangePriceChoice = (value?: string) => setPriceChoice(value as O<PriceChoice>)

    const onChangeSaleYield = (value?: FieldValue) => setSaleYield(value as O<number>)

    const fields: FormField[] = [
        {
            name: 'info-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Información" />
        },
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        SupplierField({
            value: supplierId,
            productSupplier: true,
            onCreateNew: onCreateSupplier
        }),
        {
            name: 'supplierCode',
            type: FieldType.TEXT,
            label: 'Código Interno'
        },
        {
            name: 'categoryId',
            type: FieldType.TABLE,
            table: TableName.CATEGORIES,
            label: 'Categoría',
            onChange: onChangeCategory
        },
        {
            name: 'imageUrl',
            type: FieldType.IMAGE,
            label: 'Imagen',
            loading: !loaded,
            render: () => <ImageField
                id="imageUrl"
                value={imageFile}
                onChange={setImageFile}
            />
        },
        {
            name: 'description',
            type: FieldType.TEXT,
            label: 'Descripción',
            render: () => <TextArea
                id="description"
                defaultValue={description}
                optional
                onChange={setDescription}
            />
        },
        {
            name: 'measures-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Medidas" />
        },
        {
            name: 'type',
            type: FieldType.SELECT,
            label: 'Tipo de Producto',
            defaultValue: type,
            options: Object.values(ProductType).map(type => option(type, Labels.productTypeLabel(type))),
            disabled: !createMode,
            onChange: onChangeType
        },
        {
            name: 'measure',
            type: FieldType.NUMBER,
            label: 'Medida',
            hint: 'Medida principal de stock.',
            render: () => (
                <div className={styles.measure}>
                    {type !== ProductType.BULK && (
                        <NumberField
                            id="units"
                            value={units}
                            min={0.01}
                            disabled={type === ProductType.PACKAGE}
                            className={styles.units}
                            onChange={onChangeUnits}
                        />
                    )}
                    <div className={styles.unitType}>
                        <SelectField
                            id="unitTypeId"
                            value={unitTypeId}
                            options={unitTypeOptions}
                            disabled={!createMode || type === ProductType.PACKAGE}
                            onChange={onChangeUnitType}
                        />
                    </div>
                </div>
            )
        },
        {
            name: 'measures',
            type: FieldType.TABLE,
            label: 'Medidas Adicionales',
            render: () => (
                <Validatable validations={toList(measuresValidation)}>
                    <MeasuresTable
                        title={measuresTitle}
                        rows={measuresData}
                        collapsable
                        onCreate={onAddMeasure()}
                        onEditRow={onEditMeasure()}
                        onRemoveRow={onRemoveMeasure()}
                    />
                </Validatable>
            )
        },
        {
            name: 'loted',
            type: FieldType.BOOLEAN,
            label: 'Contiene Partidas',
            onChange: onChangeLoted
        },
        {
            name: 'allowNegativeStock',
            type: FieldType.BOOLEAN,
            label: 'Habilitar Venta Sin Stock',
            value: !loted && (allowNegativeStock || isNegativeStockCategory),
            disabled: loted || isNegativeStockCategory,
            hint: loted ?
                'No disponible para productos con partidas.' :
                isNegativeStockCategory ?
                    'Habilitado por su categoría (Configuración de Productos → Venta Sin Stock).' :
                    'Permite vender este producto por más que no tenga stock disponible, previa confirmación. Útil para productos de alta rotación o con compras entrantes.',
            onChange: onChangeAllowNegativeStock
        },
        {
            name: 'price-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Precios" />
        },
        {
            name: 'basePurchasePrice',
            type: FieldType.PRICE,
            value: basePurchasePrice,
            label: hasBonuses ? 'Precio Base' : 'Precio de Compra',
            hint: purchasePriceHint,
            loading: !loaded,
            onChange: onChangeBasePurchasePrice
        },
        {
            name: 'hasBonuses',
            type: FieldType.BOOLEAN,
            label: 'Bonificaciones',
            value: hasBonuses,
            onChange: onChangeHasBonuses
        },
        {
            name: 'vatTaxId',
            type: FieldType.TABLE,
            table: TableName.TAXES,
            value: vatTaxId,
            label: 'Condición de IVA',
            options: vatTaxOptions,
            loading: !loaded,
            onChange: onChangeVatTax
        },
        {
            name: 'priceChoice',
            type: FieldType.SELECT,
            label: 'Definición del Precio',
            loading: !loaded,
            render: () => (
                <RadioField
                    id="priceChoice"
                    value={priceChoice}
                    options={priceChoiceOptions}
                    onChange={onChangePriceChoice}
                />
            )
        },
        {
            name: 'saleYield',
            type: FieldType.PERCENTAGE,
            label: 'Rentabilidad de Venta',
            hint: 'Calculado para el precio de lista base.',
            render: () => priceChoice === PriceChoice.PRODUCT_YIELD ? (
                <PercentField
                    id="saleYield"
                    value={saleYield}
                    round={false}
                    onChange={onChangeSaleYield}
                />
            ) : (
                <StaticValue
                    type={FieldType.PERCENTAGE}
                    value={saleYield}
                />
            )
        },
        {
            name: 'salePrice',
            type: FieldType.PRICE,
            label: 'Precio de Lista (sin IVA)',
            render: () => priceChoice === PriceChoice.PRODUCT_PRICE ? (
                <PriceField
                    id="salePrice"
                    value={salePrice}
                    min={pricesHooks.basePrice(purchasePrice || 0, 0.01)}
                    onChange={setSalePrice}
                />
            ) : (
                <StaticValue
                    type={FieldType.PRICE}
                    value={salePrice}
                />
            )
        },
        {
            name: 'salePriceWithVat',
            type: FieldType.PRICE,
            label: 'Precio de Lista (con IVA)',
            render: () => priceChoice === PriceChoice.PRODUCT_PRICE_WITH_VAT ? (
                <PriceField
                    id="salePriceWithVat"
                    value={salePriceWithVat}
                    min={pricesHooks.basePrice(purchasePrice || 0, 0.01, vatTaxId)}
                    onChange={setSalePriceWithVat}
                />
            ) : (
                <StaticValue
                    type={FieldType.PRICE}
                    value={salePriceWithVat}
                />
            )
        }
    ]
    if (hasBonuses) {
        const bonusesField = {
            name: 'bonuses',
            type: FieldType.TABLE,
            label: ' ',
            render: () => (
                <DiscountTable
                    bonusesData={bonusesData}
                    onCreate={onAddBonus}
                    onEditBonus={onEditBonus}
                    onRemoveBonus={onRemoveBonus}
                />
            )
        }
        const purchasePriceWithBonusesField = {
            name: 'purchasePrice',
            type: FieldType.PRICE,
            label: 'Precio de Compra',
            render: () => <StaticValue type={FieldType.PRICE} value={purchasePrice} />
        }
        fields.splice(15, 0, bonusesField, purchasePriceWithBonusesField)
    }
    if (type === ProductType.PACKAGE) {
        const secondaryMeasureField = {
            name: 'secondaryMeasure',
            type: FieldType.NUMBER,
            label: 'Medida Secundaria',
            hint: 'Rendimiento de la caja.',
            render: () => (
                <div className={styles.measure}>
                    <NumberField
                        id="secondaryUnits"
                        value={secondaryUnits}
                        min={0.01}
                        className={styles.units}
                        onChange={onChangeSecondaryUnits}
                    />
                    <div className={styles.unitType}>
                        <SelectField
                            id="secondaryUnitTypeId"
                            value={secondaryUnitTypeId}
                            options={unitTypeOptions}
                            disabled
                        />
                    </div>
                </div>
            )
        }
        const unitMeasuresField = {
            name: 'unitMeasures',
            type: FieldType.TABLE,
            label: ' ',
            render: () => (
                <MeasuresTable
                    title='Medidas de 1 unidad'
                    rows={unitMeasuresData}
                    collapsable
                    onCreate={onAddMeasure(false)}
                    onEditRow={onEditMeasure(false)}
                    onRemoveRow={onRemoveMeasure(false)}
                />
            )
        }
        fields.splice(10, 0, secondaryMeasureField)
        fields.splice(12, 0, unitMeasuresField)
    }
    if (type !== ProductType.BULK) {
        const measureLabelField = {
            name: 'measureLabel',
            type: FieldType.SELECT,
            label: 'Descriptor de Stock',
            value: measureLabel,
            hint: 'Palabra a utilizar en el detalle de stock del producto.',
            options: Object.values(MeasureLabel).map(label => option(label, getMeasureLabel(label, true))),
            optional: true,
            disabled: type === ProductType.PACKAGE,
            narrow: true,
            onChange: onChangeMeasureLabel
        }
        fields.splice(type === ProductType.PACKAGE ? 13 : 11, 0, measureLabelField)
    }

    const hasCost = purchasePrice > 0
    const hiddenFields = canSeeCost ? [] : [...COST_FIELDS, ...(hasCost ? [] : LIST_PRICE_FIELDS)]
    const visibleFields = fields.filter(field => !hiddenFields.includes(field.name))
    if (!canSeeCost && !hasCost) {
        visibleFields.push({
            name: 'pendingSalePrice',
            type: FieldType.TEXT,
            label: 'Precio de Lista',
            render: () => <>Sin precio: Compras debe cargar el precio de compra para poder definir el precio de lista.</>
        })
    }

    const onSubmitSupplier = (supplier: Supplier) => setSupplierId(supplier.id)

    const validate = (): boolean => {
        let isValid = true
        const unitType = unitTypeLoaders().findUnitType(unitTypeId)
        const weight = measuresData.find(measureData => measureData.type === MeasureType.WEIGHT)
        const packageUnits = measuresData.find(measureData => measureData.type === MeasureType.PACKAGE_UNITS)
        if (type !== ProductType.BULK && !weight && unitType?.category !== UnitTypeCategory.WEIGHT) {
            setMeasuresValidation('Debe agregar la medida Peso del producto')
            isValid = false
        } else if (type === ProductType.PACKAGE && !packageUnits) {
            setMeasuresValidation('Debe agregar la medida Unidades en Caja del producto')
            isValid = false
        }
    
        return isValid
    }

    const onSubmit = (productData: Data) => {
        if (validate()) {
            submitProduct(
                // Se guarda el valor propio del producto, no el que se ve si está habilitado por su categoría
                { ...productData, allowNegativeStock },
                code,
                priceChoice!,
                basePurchasePrice ?? 0,
                unitTypeId!,
                units,
                secondaryUnits,
                secondaryUnitTypeId,
                measuresData,
                unitMeasuresData,
                measureLabel,
                bonusesData,
                imageFile,
                description,
                saleYield
            )
        }
    }

    const onCancel = () => navigate(-1)

    const onSaveMeasure = (newData: Data) => {
        const data = isCurrentMainMeasure ? measuresData : unitMeasuresData
        const newMeasuresData = currentMeasure ?
            data.map(measureData => measureData.type === currentMeasure.type ? newData : measureData) :
            [newData, ...data]
        const setData = isCurrentMainMeasure ? setMeasuresData : setUnitMeasuresData
        setData(newMeasuresData)
    }

    const onSaveBonus = (newData: Data) => {
        const newBonusesData = currentBonus ?
            bonusesData.map(bonusData => bonusData.name === currentBonus.name ? newData : bonusData) :
            [newData, ...bonusesData]
        setBonusesData(newBonusesData)
    }

    return (
        <>
            <DataTypeForm
               formId="product-form"
               fields={visibleFields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            <QuickFormModal title="Crear Proveedor">
                <SupplierQuickForm
                    productSupplier={true}
                    onSubmit={onSubmitSupplier}
                />
            </QuickFormModal>
            <MeasureFormModal
                measureData={currentMeasure}
                measureTypes={measureTypes}
                onSubmit={onSaveMeasure}
            />
            <DiscountFormModal
                defaultData={currentBonus}
                usedNames={bonusesData.map(bonusData => bonusData.name!)}
                onSubmit={onSaveBonus}
            />
        </>
    )
}
